import { useCallback } from "react";
import {
  ADS_CAMPAIGN_STATUS_DRAFT,
  ADS_KEYS_CREATIVES,
  ADS_KEYS_TEXT,
  ADS_OBJECT_TYPES,
  GOOGLE,
} from "../utils/constants";
import { waitDelay } from "../utils/date";
import { getUniqueID } from "../utils/numbers";
import _ from "lodash";
import { getKeyDatabase } from "../services/public";
import { useDispatchApp } from "../lib/redux";

const commonParamsSetValue = {
  shouldDirty: true,
  shouldValidate: true,
};

const getFormattedTexts = (texts = []) => {
  if (!texts) return [];
  return texts.map((text) => {
    if (typeof text === "string") {
      return {
        value: text,
        id: getUniqueID(),
      };
    }
    return text;
  });
};

// Helper function to check if a value is valid JSON
const isValidJSON = (value) => {
  if (typeof value !== "string") return false;
  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    console.error("Error parsing JSON value in isValidJSON:", error.message);
    return false;
  }
};

// Helper function to safely parse JSON or return original value
const parseValueSafely = (value, onlyObjects) => {
  if (isValidJSON(value)) {
    try {
      const parsedValue = JSON.parse(value);
      if (onlyObjects && !_.isObject(parsedValue) && !_.isArray(parsedValue)) {
        return value;
      }
      return parsedValue;
    } catch (error) {
      console.error(
        "Error parsing JSON value in parseValueSafely:",
        error.message
      );
      return value;
    }
  }
  return value;
};
const getFormattedCreateValues = ({ campaignID, change, dispatch, value }) => {
  const { platform, entity, adGroupID } = change;
  const newEntityID = dispatch(getKeyDatabase());

  const commonValues = {
    ...value,
    creationTime: new Date().getTime(),
    creationTimeNegative: -new Date().getTime(),
    status: ADS_CAMPAIGN_STATUS_DRAFT,
    platform,
    id: newEntityID,
    idPlatform: null,
    campaignID,
  };
  Object.keys(commonValues).forEach((key) => {
    if (ADS_KEYS_TEXT.includes(key)) {
      const textFormatted = getFormattedTexts(commonValues[key]);
      commonValues[key] = textFormatted;
    }
  });

  if (entity === ADS_OBJECT_TYPES.adGroup) {
    if (!_.isEmpty(value.ads)) {
      commonValues.ads = value.ads.map((ad) => {
        const newAdID = dispatch(getKeyDatabase());
        const adFormatted = {
          ...ad,
          id: newAdID,
          idPlatform: null,
          creationTime: new Date().getTime(),
          creationTimeNegative: -new Date().getTime(),
          status: ADS_CAMPAIGN_STATUS_DRAFT,
          platform,
          campaignID,
          adGroupID: newEntityID,
        };
        Object.keys(adFormatted).forEach((key) => {
          if (ADS_KEYS_TEXT.includes(key)) {
            const textFormatted = getFormattedTexts(ad[key]);
            adFormatted[key] = textFormatted;
          }
        });

        return adFormatted;
      });
    }
  }

  if (entity === ADS_OBJECT_TYPES.ad) {
    commonValues.adGroupID = adGroupID;
  }

  return commonValues;
};

const useApplyOptimizationsCampaign = ({ methods, campaignID }) => {
  const { onChangeSectionSelected, getValues, setValue } = methods;
  const dispatch = useDispatchApp();

  // Helper function to find adGroup index by ID
  const findAdGroupIndex = useCallback((adsGroups, adGroupID) => {
    return (adsGroups || []).findIndex(
      (group) => group.id === adGroupID || group.idPlatform === adGroupID
    );
  }, []);
  const findAssetsGroupIndex = useCallback((assetsGroups, assetsGroupID) => {
    return (assetsGroups || []).findIndex(
      (group) =>
        group.id === assetsGroupID || group.idPlatform === assetsGroupID
    );
  }, []);

  // Helper function to find ad index by ID within a group
  const findAdIndex = useCallback((ads, adID) => {
    return (ads || []).findIndex(
      (ad) => ad.id === adID || ad.idPlatform === adID
    );
  }, []);

  const getTrueAdGroupID = useCallback((change) => {
    const { adGroupID } = change;
    const adsGroups = getValues("adsGroups");
    const adGroupIndex = findAdGroupIndex(adsGroups, adGroupID);
    if (adGroupIndex >= 0) {
      return adsGroups[adGroupIndex].id || undefined;
    }
    return undefined;
    //eslint-disable-next-line
  }, []);

  const getTrueAssetsGroupID = useCallback((change) => {
    const isCreateAction = change.operation === "create";
    const { assetsGroupID } = change;
    const assetsGroups = getValues("assetsGroups");

    if (isCreateAction) {
      //select last assets group
      return assetsGroups?.[assetsGroups?.length - 1]?.id || undefined;
    }

    const assetsGroupIndex = findAssetsGroupIndex(assetsGroups, assetsGroupID);
    if (assetsGroupIndex >= 0) {
      return assetsGroups[assetsGroupIndex].id || undefined;
    }
    return undefined;
    //eslint-disable-next-line
  }, []);

  const getTrueAdID = useCallback((change) => {
    const isCreateAction = change.operation === "create";
    const { adGroupID, adID } = change;
    const adsGroups = getValues("adsGroups");
    const groupIndex = findAdGroupIndex(adsGroups, adGroupID);
    if (groupIndex < 0) return undefined;

    if (isCreateAction) {
      //select last ad
      return (
        adsGroups?.[groupIndex]?.ads?.[adsGroups?.[groupIndex]?.ads?.length - 1]
          ?.id || undefined
      );
    }

    const adIndex = findAdIndex(adsGroups[groupIndex].ads, adID);
    if (adIndex < 0) return undefined;

    return adsGroups[groupIndex].ads[adIndex].id || undefined;

    //eslint-disable-next-line
  }, []);

  // Resolve the base path for the entity
  const resolveBasePath = useCallback(
    (formValues, change) => {
      const {
        platform,
        operation,
        entity,
        adGroupID,
        adID,
        assetsGroupID,
        field,
      } = change;

      if (entity === ADS_OBJECT_TYPES.campaign) {
        if (field === "status") {
          return `states.${platform}`;
        }
        return `platformsData.${platform}`;
      }

      if (entity === ADS_OBJECT_TYPES.adGroup) {
        if (operation === "create") {
          return `adsGroups`;
        }
        const groupIndex = findAdGroupIndex(formValues.adsGroups, adGroupID);
        return groupIndex >= 0 ? `adsGroups.[${groupIndex}]` : undefined;
      }

      if (entity === ADS_OBJECT_TYPES.assetsGroup) {
        if (operation === "create") {
          return `assetsGroups`;
        }
        const groupIndex = findAssetsGroupIndex(
          formValues.assetsGroups,
          assetsGroupID
        );
        return groupIndex >= 0 ? `assetsGroups.[${groupIndex}]` : undefined;
      }

      if (entity === ADS_OBJECT_TYPES.ad) {
        const groupIndex = findAdGroupIndex(formValues.adsGroups, adGroupID);
        if (groupIndex < 0) return undefined;

        if (operation === "create") {
          return `adsGroups.[${groupIndex}].ads`;
        }

        const adIndex = findAdIndex(formValues.adsGroups[groupIndex].ads, adID);
        return adIndex >= 0
          ? `adsGroups.[${groupIndex}].ads.[${adIndex}]`
          : undefined;
      }

      return undefined;
    },
    [findAdGroupIndex, findAdIndex, findAssetsGroupIndex]
  );

  // Helper function to add animation to a field
  const animateField = useCallback(
    (element, animationType = "highlight", pathField = "") => {
      if (!element) return;

      // Use parent element if requested
      let targetElement = element;
      if (element.parentElement) {
        targetElement = element.parentElement;
      }

      // Remove any existing animation classes
      targetElement.classList.remove(
        "field-highlight",
        "field-pulse",
        "field-glow"
      );

      // Force reflow to ensure class removal takes effect
      targetElement.offsetHeight;

      // Add the animation class
      targetElement.classList.add(`field-${animationType}`);

      // Remove the class after animation completes
      setTimeout(() => {
        let newTargetElement = document.getElementById(pathField);
        if (newTargetElement) {
          if (newTargetElement.parentElement) {
            newTargetElement = newTargetElement.parentElement;
          }
          newTargetElement.classList.remove(`field-${animationType}`);
        }
      }, 10000);
    },
    []
  );

  const onDoChangeSectionAndFocusField = useCallback(
    async (change, optimization) => {
      const { platform, entity, field } = change;
      const campaignValues = getValues();
      const path = resolveBasePath(campaignValues, change);

      if (!path) {
        throw new Error("Could not resolve base path for change:", change);
      }

      if (entity === ADS_OBJECT_TYPES.campaign) {
        onChangeSectionSelected({
          platform,
          adGroupID: undefined,
          adID: undefined,
          assetsGroupID: undefined,
        });
      }

      if (entity === ADS_OBJECT_TYPES.adGroup) {
        const trueAdGroupID = getTrueAdGroupID(change);
        onChangeSectionSelected({
          platform,
          adGroupID: trueAdGroupID,
          adID: undefined,
          assetsGroupID: undefined,
        });
      }

      if (entity === ADS_OBJECT_TYPES.assetsGroup) {
        const isCreateAction = change.operation === "create";
        const creativeOptimization =
          isCreateAction && optimization
            ? {
                title: optimization.title,
                action: optimization.action,
                field,
                formPath: path,
              }
            : undefined;
        const trueAssetsGroupID = getTrueAssetsGroupID(change);
        onChangeSectionSelected({
          platform,
          adGroupID: undefined,
          adID: undefined,
          assetsGroupID: trueAssetsGroupID,
          creativeOptimization,
        });
      }

      if (entity === ADS_OBJECT_TYPES.ad) {
        const isCreateAction = change.operation === "create";
        const trueAdGroupID = getTrueAdGroupID(change);
        const trueAdID = getTrueAdID(change);
        const creativeOptimization =
          isCreateAction && optimization
            ? {
                title: optimization.title,
                action: optimization.action,
                field,
                formPath: path,
              }
            : undefined;

        onChangeSectionSelected({
          platform,
          adGroupID: trueAdGroupID,
          adID: trueAdID,
          assetsGroupID: undefined,
          creativeOptimization,
        });
      }
      await waitDelay(1000);
      if (field) {
        const fieldInDoom = document.getElementById(`${path}.${field}`);
        if (fieldInDoom) {
          // Scroll to the field first
          fieldInDoom?.scrollIntoView({ behavior: "smooth" });
        }
      }
    },
    // eslint-disable-next-line
    [animateField]
  );

  // Apply a single change to the form
  const applyChange = useCallback(
    (change, optimization) => {
      const formValues = getValues();
      const basePath = resolveBasePath(formValues, change);

      if (!basePath) {
        throw new Error("Could not resolve base path for change:", change);
      }

      let { operation, entity, field, itemsByID, itemsByValue, platform } =
        change;

      let value = parseValueSafely(change.value);
      if (_.isArray(value)) {
        value = value.map((item) => {
          return parseValueSafely(item, true);
        });
      }

      let fullPath = basePath;
      const isUpdateStatusCampaign =
        field === "status" && entity === ADS_OBJECT_TYPES.campaign;
      const isCreateOperation = operation === "create";

      if (!isUpdateStatusCampaign && !isCreateOperation) {
        fullPath = basePath ? `${basePath}.${field}` : field;
      }

      if (ADS_KEYS_CREATIVES.includes(field)) {
        const creativeOptimization = {
          title: optimization.title,
          action: optimization.action,
          field,
          formPath: basePath,
        };

        if (entity === ADS_OBJECT_TYPES.ad) {
          const trueAdGroupID = getTrueAdGroupID(change);
          const trueAdID = getTrueAdID(change);
          onChangeSectionSelected({
            platform: change.platform,
            adGroupID: trueAdGroupID,
            adID: trueAdID,
            assetsGroupID: undefined,
            creativeOptimization,
          });
        }

        if (entity === ADS_OBJECT_TYPES.assetsGroup) {
          const trueAssetsGroupID = getTrueAssetsGroupID(change);
          onChangeSectionSelected({
            platform: change.platform,
            adGroupID: undefined,
            adID: undefined,
            assetsGroupID: trueAssetsGroupID,
            creativeOptimization,
          });
        }
        if (entity === ADS_OBJECT_TYPES.adGroup) {
          const trueAdGroupID = getTrueAdGroupID(change);
          onChangeSectionSelected({
            platform: change.platform,
            adGroupID: trueAdGroupID,
            adID: undefined,
            assetsGroupID: undefined,
            creativeOptimization,
          });
        }
        return;
      }

      switch (operation) {
        case "create": {
          const currentValues = getValues(fullPath) || [];
          const formattedCreateValues = getFormattedCreateValues({
            change,
            campaignID,
            dispatch,
            value,
          });
          setValue(
            fullPath,
            [...currentValues, formattedCreateValues],
            commonParamsSetValue
          );
          break;
        }
        case "update": {
          if (ADS_KEYS_TEXT.includes(field)) {
            let valueFormatted = getFormattedTexts(value);
            if (platform === GOOGLE) {
              valueFormatted = valueFormatted.sort(
                (a, b) => a.value.length - b.value.length
              );
            }
            setValue(fullPath, valueFormatted, commonParamsSetValue);
          } else {
            setValue(fullPath, value, commonParamsSetValue);
          }

          break;
        }

        case "add": {
          const currentValues = getValues(fullPath) || [];
          const newItems = Array.isArray(value) ? value : [value];

          // Deduplicate by ID if items have IDs
          const existingIds = new Set(
            currentValues.map((item) => item.id).filter(Boolean)
          );
          const itemsToAdd = newItems.filter((item) => {
            const itemFormatted = parseValueSafely(item);
            return !itemFormatted?.id || !existingIds.has(itemFormatted?.id);
          });

          if (ADS_KEYS_TEXT.includes(field)) {
            const valueFormatted = getFormattedTexts(itemsToAdd);
            let allItems = [...currentValues, ...valueFormatted];
            if (platform === GOOGLE) {
              allItems = allItems.sort(
                (a, b) => a.value.length - b.value.length
              );
            }
            setValue(fullPath, allItems, commonParamsSetValue);
          } else {
            setValue(
              fullPath,
              [...itemsToAdd, ...currentValues],
              commonParamsSetValue
            );
          }
          break;
        }

        case "remove": {
          const currentItems = getValues(fullPath) || [];
          const idsToRemove = new Set(itemsByID || []);
          const valuesToRemove = new Set(itemsByValue || []);

          const filteredItems = currentItems.filter((item) => {
            const shouldRemoveById = item?.id && idsToRemove.has(item?.id);
            const shouldRemoveByValue =
              (item?.value && valuesToRemove.has(item?.value)) ||
              (item && valuesToRemove.has(item));
            const shouldRemoveByName =
              item?.name && valuesToRemove.has(item?.name);

            return !(
              shouldRemoveById ||
              shouldRemoveByValue ||
              shouldRemoveByName
            );
          });

          setValue(fullPath, filteredItems, commonParamsSetValue);
          break;
        }

        case "replaceAll": {
          if (ADS_KEYS_TEXT.includes(field)) {
            const valueFormatted = getFormattedTexts(value);
            setValue(fullPath, valueFormatted, commonParamsSetValue);
          } else {
            setValue(fullPath, value, commonParamsSetValue);
          }
          break;
        }

        default:
          console.log("Unknown operation:", operation);
      }
    },

    //eslint-disable-next-line
    [resolveBasePath, methods]
  );

  // Apply multiple optimizations
  const applyOptimizations = useCallback(
    (optimizations) => {
      if (!Array.isArray(optimizations)) {
        console.log("Optimizations must be an array");
        return true;
      }
      let isApplied = true;

      optimizations.forEach((optimization) => {
        if (optimization.changes && Array.isArray(optimization.changes)) {
          optimization.changes.forEach((change, index) => {
            try {
              applyChange(change, optimization);

              //is last change
              if (index === optimization.changes.length - 1) {
                onDoChangeSectionAndFocusField(change, optimization);
              }
            } catch (error) {
              console.log("Error applying change:", change, error);
              isApplied = false;
            }
          });
        }
      });

      return isApplied;
    },
    [applyChange, onDoChangeSectionAndFocusField]
  );

  return {
    applyChange,
    applyOptimizations,
  };
};

export default useApplyOptimizationsCampaign;
